import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { salesService } from '../services';
import { formatCurrency } from '../utils/formatters';
import PageHeader from '../components/ui/PageHeader';
import StatGrid from '../components/ui/StatGrid';
import Icon from '../components/nav/Icon';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Analytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('12'); // 12 weeks default
  const [appMessage, setAppMessage] = useState({ text: '', type: '' });

  // Fetch analytics data
  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const weeklyData = await salesService.getWeekly();

      // Process data for analytics
      const processedData = processAnalyticsData(weeklyData || [], parseInt(timeRange));
      setAnalyticsData(processedData);
    } catch (error) {
      // Only log errors to console, don't show to user
      console.error('Error fetching analytics data:', error);
      // Set empty data instead of error
      setAnalyticsData(null);
    } finally {
      setLoading(false);
    }
  };

  // Process data for analytics
  const processAnalyticsData = (weeklyData, weeks) => {
    const recentWeeks = weeklyData.slice(0, weeks);
    const allWeeks = weeklyData;

    // 1. Revenue Growth Trend
    const revenueTrend = recentWeeks.map(week => ({
      week: `Week ${week.weekNumber}`,
      revenue: week.totalAmount,
      sales: week.totalSales
    }));

    // 2. Sales Performance by Week
    const weeklyPerformance = recentWeeks.map(week => ({
      week: `W${week.weekNumber}`,
      revenue: week.totalAmount,
      sales: week.totalSales,
      average: week.totalSales > 0 ? week.totalAmount / week.totalSales : 0
    }));

    // 3. Product Performance Analysis
    const productPerformance = analyzeProductPerformance(allWeeks);

    // 4. Sales Velocity & Forecasting
    const salesVelocity = calculateSalesVelocity(recentWeeks);

    // 5. Customer Insights
    const customerInsights = analyzeCustomerInsights(allWeeks);

    return {
      revenueTrend,
      weeklyPerformance,
      productPerformance,
      salesVelocity,
      customerInsights,
      summary: {
        totalRevenue: allWeeks.reduce((sum, week) => sum + week.totalAmount, 0),
        totalSales: allWeeks.reduce((sum, week) => sum + week.totalSales, 0),
        averageRevenue: allWeeks.length > 0 ? allWeeks.reduce((sum, week) => sum + week.totalAmount, 0) / allWeeks.length : 0,
        growthRate: calculateGrowthRate(recentWeeks),
        bestWeek: recentWeeks.reduce((best, week) => week.totalAmount > best.totalAmount ? week : best, recentWeeks[0])
      }
    };
  };

  // Analyze product performance
  const analyzeProductPerformance = (weeks) => {
    const productStats = {};

    weeks.forEach(week => {
      week.sales?.forEach(sale => {
        const productName = sale.products?.name || sale.productType || 'Unknown';
        if (!productStats[productName]) {
          productStats[productName] = { revenue: 0, sales: 0, quantity: 0 };
        }
        productStats[productName].revenue += sale.amount || 0;
        productStats[productName].sales += 1;
        productStats[productName].quantity += sale.quantity || 1;
      });
    });

    return Object.entries(productStats)
      .map(([name, stats]) => ({
        name,
        revenue: stats.revenue,
        sales: stats.sales,
        quantity: stats.quantity,
        averagePrice: stats.sales > 0 ? stats.revenue / stats.sales : 0
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5); // Top 5 products
  };

  // Calculate sales velocity and forecasting
  const calculateSalesVelocity = (weeks) => {
    if (weeks.length < 2) return { velocity: 0, forecast: 0, trend: 'stable' };

    const recentWeeks = weeks.slice(0, 4); // Last 4 weeks
    const totalRevenue = recentWeeks.reduce((sum, week) => sum + week.totalAmount, 0);
    const averageWeeklyRevenue = totalRevenue / recentWeeks.length;

    // Calculate growth trend
    const firstHalf = recentWeeks.slice(0, 2).reduce((sum, week) => sum + week.totalAmount, 0);
    const secondHalf = recentWeeks.slice(2).reduce((sum, week) => sum + week.totalAmount, 0);
    const growthRate = secondHalf > 0 ? ((secondHalf - firstHalf) / firstHalf) * 100 : 0;

    // Forecast next 4 weeks
    const forecast = averageWeeklyRevenue * 4 * (1 + (growthRate / 100));

    return {
      velocity: averageWeeklyRevenue,
      forecast,
      trend: growthRate > 5 ? 'growing' : growthRate < -5 ? 'declining' : 'stable',
      growthRate
    };
  };

  // Analyze customer insights
  const analyzeCustomerInsights = (weeks) => {
    const customerStats = {};
    let totalCustomers = 0;
    let repeatCustomers = 0;

    weeks.forEach(week => {
      week.sales?.forEach(sale => {
        const customerKey = `${sale.customerName}-${sale.customerPhone}`;
        if (!customerStats[customerKey]) {
          customerStats[customerKey] = { purchases: 0, totalSpent: 0 };
          totalCustomers++;
        } else {
          repeatCustomers++;
        }
        customerStats[customerKey].purchases += 1;
        customerStats[customerKey].totalSpent += sale.amount || 0;
      });
    });

    const customerValues = Object.values(customerStats);
    const averageCustomerValue = customerValues.reduce((sum, customer) => sum + customer.totalSpent, 0) / customerValues.length;
    const repeatRate = totalCustomers > 0 ? (repeatCustomers / totalCustomers) * 100 : 0;

    return {
      totalCustomers,
      repeatCustomers,
      repeatRate,
      averageCustomerValue,
      topCustomers: customerValues
        .sort((a, b) => b.totalSpent - a.totalSpent)
        .slice(0, 5)
    };
  };

  // Calculate growth rate
  const calculateGrowthRate = (weeks) => {
    if (weeks.length < 2) return 0;
    const firstHalf = weeks.slice(0, Math.floor(weeks.length / 2));
    const secondHalf = weeks.slice(Math.floor(weeks.length / 2));

    const firstHalfRevenue = firstHalf.reduce((sum, week) => sum + week.totalAmount, 0);
    const secondHalfRevenue = secondHalf.reduce((sum, week) => sum + week.totalAmount, 0);

    return firstHalfRevenue > 0 ? ((secondHalfRevenue - firstHalfRevenue) / firstHalfRevenue) * 100 : 0;
  };

  useEffect(() => {
    fetchAnalyticsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  const handleRefresh = async () => {
    try {
      await fetchAnalyticsData();
      setAppMessage({ text: 'Analytics data refreshed successfully!', type: 'success' });
      setTimeout(() => setAppMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      console.error("Failed to refresh analytics:", error);
      // Only log errors to console
      console.error('Failed to refresh analytics data:', error);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading analytics...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6">
        <PageHeader
          title="Analytics"
          subtitle="How sales are trending"
          actions={
            <>
              <select
                aria-label="Time range"
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="h-10 rounded-lg border border-slate-200 bg-white pl-3 pr-8 text-sm font-medium text-slate-700 focus:ring-blue-500"
              >
                <option value="4">4 weeks</option>
                <option value="8">8 weeks</option>
                <option value="12">12 weeks</option>
                <option value="16">16 weeks</option>
              </select>
              <button
                type="button"
                onClick={handleRefresh}
                aria-label="Refresh"
                className="h-10 w-10 md:w-auto md:px-4 inline-flex items-center justify-center gap-2 rounded-lg bg-white border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <Icon name="refresh" className="w-5 h-5" />
                <span className="hidden md:inline">Refresh</span>
              </button>
            </>
          }
        />

        {/* Global App Message Display */}
        {appMessage.text && (
          <div className={`p-4 rounded-lg border ${appMessage.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : appMessage.type === 'error'
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}>
            {appMessage.text}
          </div>
        )}

        <StatGrid
          items={[
            { label: 'Revenue', value: formatCurrency(analyticsData?.summary.totalRevenue || 0), tone: 'good' },
            { label: 'Sales', value: analyticsData?.summary.totalSales || 0 },
            { label: 'Growth', value: `${(analyticsData?.summary.growthRate || 0).toFixed(1)}%`, tone: (analyticsData?.summary.growthRate || 0) > 0 ? 'good' : 'bad' },
            { label: 'Avg per week', value: formatCurrency(analyticsData?.summary.averageRevenue || 0) },
          ]}
        />

        {/* Metric 1: Revenue Growth Trend */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 md:p-6">
          <div className="flex items-center justify-between gap-2 mb-4 md:mb-6">
            <h2 className="text-base md:text-xl font-semibold text-gray-800">Revenue Growth Trend</h2>
            <div className="hidden md:flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Revenue Trend</span>
            </div>
          </div>
          <div className="h-56 md:h-80">
            <Line
              data={{
                labels: analyticsData?.revenueTrend.map(item => item.week) || [],
                datasets: [
                  {
                    label: 'Revenue',
                    data: analyticsData?.revenueTrend.map(item => item.revenue) || [],
                    borderColor: 'rgb(59, 130, 246)',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    tension: 0.4,
                    fill: true,
                  },
                  {
                    label: 'Sales Count',
                    data: analyticsData?.revenueTrend.map(item => item.sales) || [],
                    borderColor: 'rgb(16, 185, 129)',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    tension: 0.4,
                    yAxisID: 'y1',
                  }
                ]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                  mode: 'index',
                  intersect: false,
                },
                scales: {
                  y: {
                    type: 'linear',
                    display: true,
                    position: 'left',
                    title: {
                      display: true,
                      text: 'Revenue ($)'
                    }
                  },
                  y1: {
                    type: 'linear',
                    display: true,
                    position: 'right',
                    title: {
                      display: true,
                      text: 'Sales Count'
                    },
                    grid: {
                      drawOnChartArea: false,
                    },
                  },
                },
                plugins: {
                  legend: {
                    position: 'top',
                  },
                }
              }}
            />
          </div>
        </div>

        {/* Metric 2: Sales Performance by Week */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 md:p-6">
          <div className="flex items-center justify-between gap-2 mb-4 md:mb-6">
            <h2 className="text-base md:text-xl font-semibold text-gray-800">Weekly Performance Analysis</h2>
            <div className="hidden md:flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Performance Metrics</span>
            </div>
          </div>
          <div className="h-56 md:h-80">
            <Bar
              data={{
                labels: analyticsData?.weeklyPerformance.map(item => item.week) || [],
                datasets: [
                  {
                    label: 'Revenue',
                    data: analyticsData?.weeklyPerformance.map(item => item.revenue) || [],
                    backgroundColor: 'rgba(59, 130, 246, 0.8)',
                    borderColor: 'rgb(59, 130, 246)',
                    borderWidth: 1,
                  },
                  {
                    label: 'Average Sale',
                    data: analyticsData?.weeklyPerformance.map(item => item.average) || [],
                    backgroundColor: 'rgba(16, 185, 129, 0.8)',
                    borderColor: 'rgb(16, 185, 129)',
                    borderWidth: 1,
                    yAxisID: 'y1',
                  }
                ]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: {
                    type: 'linear',
                    display: true,
                    position: 'left',
                    title: {
                      display: true,
                      text: 'Revenue ($)'
                    }
                  },
                  y1: {
                    type: 'linear',
                    display: true,
                    position: 'right',
                    title: {
                      display: true,
                      text: 'Average Sale ($)'
                    },
                    grid: {
                      drawOnChartArea: false,
                    },
                  },
                },
                plugins: {
                  legend: {
                    position: 'top',
                  },
                }
              }}
            />
          </div>
        </div>

        {/* Metric 3: Product Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 md:p-6">
            <div className="flex items-center justify-between gap-2 mb-4 md:mb-6">
              <h2 className="text-base md:text-xl font-semibold text-gray-800">Top Products by Revenue</h2>
              <div className="hidden md:flex items-center space-x-2">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Product Analysis</span>
              </div>
            </div>
            <div className="space-y-4">
              {analyticsData?.productPerformance.map((product, index) => (
                <div key={product.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-sm font-bold text-purple-600">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{product.name}</p>
                      <p className="text-sm text-gray-600">{product.sales} sales</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{formatCurrency(product.revenue)}</p>
                    <p className="text-sm text-gray-600">{product.quantity} units</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-4 md:p-6">
            <div className="flex items-center justify-between gap-2 mb-4 md:mb-6">
              <h2 className="text-base md:text-xl font-semibold text-gray-800">Product Revenue Distribution</h2>
              <div className="hidden md:flex items-center space-x-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Revenue Share</span>
              </div>
            </div>
            <div className="h-56 md:h-64">
              <Doughnut
                data={{
                  labels: analyticsData?.productPerformance.map(item => item.name) || [],
                  datasets: [
                    {
                      data: analyticsData?.productPerformance.map(item => item.revenue) || [],
                      backgroundColor: [
                        'rgba(59, 130, 246, 0.8)',
                        'rgba(16, 185, 129, 0.8)',
                        'rgba(245, 158, 11, 0.8)',
                        'rgba(239, 68, 68, 0.8)',
                        'rgba(139, 92, 246, 0.8)',
                      ],
                      borderWidth: 2,
                      borderColor: '#ffffff',
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Metric 4: Sales Velocity & Forecasting */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 md:p-6">
          <div className="flex items-center justify-between gap-2 mb-4 md:mb-6">
            <h2 className="text-base md:text-xl font-semibold text-gray-800">Sales Velocity & Forecasting</h2>
            <div className="hidden md:flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Predictive Analytics</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <div className="text-center p-4 md:p-6 bg-blue-50 rounded-lg">
              <div className="text-2xl md:text-3xl font-bold text-blue-600 mb-2">
                {formatCurrency(analyticsData?.salesVelocity.velocity || 0)}
              </div>
              <p className="text-sm text-gray-600">Weekly Sales Velocity</p>
              <p className="text-xs text-gray-500 mt-1">Average revenue per week</p>
            </div>
            <div className="text-center p-4 md:p-6 bg-green-50 rounded-lg">
              <div className="text-2xl md:text-3xl font-bold text-green-600 mb-2">
                {formatCurrency(analyticsData?.salesVelocity.forecast || 0)}
              </div>
              <p className="text-sm text-gray-600">4-Week Forecast</p>
              <p className="text-xs text-gray-500 mt-1">Projected revenue</p>
            </div>
            <div className="text-center p-4 md:p-6 bg-purple-50 rounded-lg">
              <div className={`text-2xl md:text-3xl font-bold mb-2 ${analyticsData?.salesVelocity.trend === 'growing' ? 'text-green-600' :
                  analyticsData?.salesVelocity.trend === 'declining' ? 'text-red-600' : 'text-gray-600'
                }`}>
                {(analyticsData?.salesVelocity.growthRate || 0).toFixed(1)}%
              </div>
              <p className="text-sm text-gray-600">Growth Rate</p>
              <p className="text-xs text-gray-500 mt-1 capitalize">
                {analyticsData?.salesVelocity.trend || 'stable'} trend
              </p>
            </div>
          </div>
        </div>

        {/* Metric 5: Customer Insights */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 md:p-6">
          <div className="flex items-center justify-between gap-2 mb-4 md:mb-6">
            <h2 className="text-base md:text-xl font-semibold text-gray-800">Customer Insights</h2>
            <div className="hidden md:flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Customer Analysis</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Total Customers</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analyticsData?.customerInsights.totalCustomers || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">👥</span>
                </div>
              </div>
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Repeat Customers</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analyticsData?.customerInsights.repeatCustomers || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">🔄</span>
                </div>
              </div>
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Repeat Rate</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {(analyticsData?.customerInsights.repeatRate || 0).toFixed(1)}%
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">📊</span>
                </div>
              </div>
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Avg. Customer Value</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(analyticsData?.customerInsights.averageCustomerValue || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">💰</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Customers</h3>
              <div className="space-y-3">
                {analyticsData?.customerInsights.topCustomers.map((customer, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-sm font-bold text-orange-600">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Customer #{index + 1}</p>
                        <p className="text-sm text-gray-600">{customer.purchases} purchases</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">{formatCurrency(customer.totalSpent)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Analytics;
